import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsBoost from "highcharts/modules/boost";
import HighchartsExporting from "highcharts/modules/exporting";
import React, { FC, useEffect, useRef, useState } from "react";
import Exporting from "highcharts/modules/exporting";
import OfflineExporting from "highcharts/modules/offline-exporting";
import "./chartStyle.scss";

HighchartsBoost(Highcharts);
HighchartsExporting(Highcharts);
Exporting(Highcharts);
OfflineExporting(Highcharts);

declare module "highcharts" {
  interface Point {
    custom?: {
      saved?: number;
    };
  }
}

interface EnergyStackedChartComponentProps {
  xAxisCategories: any;
  SeriesType: any;
  seriesData: any;
  seriesShowInLegend: boolean;
  isShowDataLevels: boolean;
  isShowStackedColor: boolean;
  seriesName?: string;
  xAxisTitleText?: string;
  yAxisTitleText?: string;
  tooltipPrefix?: string;
  setSelectedLocation?: any;
  setActiveKey?: any;
}

const formatEnergyTooltip = function (
  this: Highcharts.TooltipFormatterContextObject,
  tooltipPrefix?: string
) {
  const savedValue = this.point?.custom?.saved ?? 0;
  const consumedValue = this.y;

  return `
    <b>${tooltipPrefix} ${this.x}</b><br/>
    <b>Energy Consumed:</b> ${consumedValue} Wh<br/>
    <b>Energy Saved:</b> ${savedValue}%`;
};

const formatEnergyDataLabel = function (this: Highcharts.PointLabelObject) {
  const value = this.y;

  // Hiding the zero values.
  if (value === 0) {
    return `<span style="color: #999;opacity: 0">${value}</span>`;
  }
  return value;
};

const EnergyStackedChartComponent: FC<EnergyStackedChartComponentProps> = ({
  xAxisCategories,
  SeriesType,
  seriesData = [],
  seriesShowInLegend,
  isShowDataLevels = true,
  isShowStackedColor = true,
  xAxisTitleText,
  yAxisTitleText,
  tooltipPrefix,
  setSelectedLocation,
  setActiveKey,
}) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const [customHighChartOptions, setCustomHighChartOptions] = useState(
    {} as Highcharts.Options
  );

  console.log("xAxisTitleText: ", xAxisTitleText);
  console.log("xAxisCategories: ", xAxisCategories);
  console.log("seriesData: ", seriesData);

  useEffect(() => {
    if (seriesData?.length) {
      const consumedSeries = seriesData.find(
        (s: any) => s?.name === "Energy Consumed" || s?.name === "EnergyConsumption"
      );
      const savedSeries = seriesData.find(
        (s: any) => s?.name === "Energy Saved"
      );

      const options: Highcharts.Options = {
        chart: {
          type: "column",
          style: {
            fontFamily: '"Cabin", Helvetica, Arial, sans-serif',
          },
          zooming: {
            type: "x",
          },
          animation: {
            duration: 1500,
          },
        },
        credits: {
          enabled: false,
        },
        xAxis: [
          {
            categories: xAxisCategories,
            title: {
              text: xAxisTitleText || "",
              style: {
                fontSize: "16px",
                fontWeight: "bold",
                textAlign: "right",
              },
            },
          },
          {
            linkedTo: 0, // Link second axis to the first one
            opposite: true,
            visible: false,
          },
        ],
        yAxis: {
          title: {
            text: yAxisTitleText || "",
            style: {
              fontSize: "16px",
              fontWeight: "bold",
            },
          },
          stackLabels: {
            enabled: true,
          },
        },
        legend: {
          align: "center", // Align the legend horizontally to the center
          verticalAlign: "bottom", // Position the legend at the bottom
          layout: "horizontal", // Arrange items horizontally
          floating: false, // Make sure the legend is placed within the chart
          backgroundColor:
            Highcharts?.defaultOptions?.legend?.backgroundColor || "white",
          borderColor: "#CCC",
          borderWidth: 1,
          shadow: false,
        },

        tooltip: {
          useHTML: true,
          formatter: function (this: Highcharts.TooltipFormatterContextObject) {
            return formatEnergyTooltip.call(this, tooltipPrefix);
          },
        },

        plotOptions: {
          column: {
            events: {
              click: function (event) {
                const category = event.point.category;
                if (setSelectedLocation) {
                  setSelectedLocation(category as string);
                }

                if (setActiveKey) {
                  setActiveKey("Sensor");
                }
              },
            },
            stacking: "normal",
            dataLabels: {
              enabled: isShowDataLevels,
              formatter: formatEnergyDataLabel,
            },
            color: isShowStackedColor ? "" : "#66b2ff",
            pointWidth: 45,
            pointPadding: 0.2,
            groupPadding: 0.1,
            borderWidth: 1,
            borderColor: "#66b2ff", // Ensure the border lines are hidden
            states: {
              hover: {
                enabled: false,
              },
            },
          },
        },

        series: [
          {
            type: SeriesType,
            name: "Energy Consumed",
            data: consumedSeries?.data?.map((y: number, index: number) => ({
              y,
              custom: {
                saved: savedSeries?.data?.[index] ?? 0,
              },
            })),
            showInLegend: seriesShowInLegend,
          },
        ],
        accessibility: {
          enabled: false,
        },
        exporting: {
          enabled: true,
          fallbackToExportServer: false,
          filename: "Energy Consumption Chart",
          sourceWidth: 1200, // Increase source dimensions
          sourceHeight: 500,
          scale: 1, // Maintain original scale
          chartOptions: {
            chart: {
              height: "100%", // Use relative height
              width: "100%", // Use relative width
            },
            title: {
              style: {
                fontSize: "14px",
              },
            },
          },
          buttons: {
            contextButton: {
              menuItems: [
                "viewFullscreen",
                "printChart",
                "separator",
                "downloadPNG",
                "downloadJPEG",
                "downloadPDF",
                "downloadSVG",
              ],
            },
          },
        },
      };

      setCustomHighChartOptions(options);
    }
  }, [seriesData, xAxisCategories, SeriesType, seriesShowInLegend]);

  return (
    <div>
      {customHighChartOptions.series ? (
        <HighchartsReact
          highcharts={Highcharts}
          options={customHighChartOptions}
          ref={chartComponentRef}
        />
      ) : (
        <div
          style={{
            height: "300px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#D9D9D9",
            fontSize: "22px",
            fontWeight: "600",
            color: "#6d6d6d",
          }}
        >
          No Data Available
        </div>
      )}
    </div>
  );
};

export default React.memo(EnergyStackedChartComponent);
